import { get, set, del, createStore } from 'idb-keyval';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';

const queryStore = createStore('alacart-query-cache', 'queries');

// Create an IDB persister for React Query with robust validation
export function createIDBPersister(key: string = 'alacart-react-query'): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      try {
        // Filter out queries marked with persist: false
        const filteredClient = {
          ...client,
          clientState: {
            ...client.clientState,
            queries: (client.clientState?.queries ?? []).filter(
              (q: any) => q.meta?.persist !== false
            ),
          },
        };
        await set(key, filteredClient, queryStore);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[IDBPersister] Failed to persist query client:', error);
        }
      }
    },
    restoreClient: async () => {
      try {
        const client = await get<PersistedClient>(key, queryStore);
        
        // Validation 1: Ensure client exists and is an object
        if (!client || typeof client !== 'object') {
          return undefined;
        }
        
        // Validation 2: Ensure clientState exists
        if (!client.clientState || typeof client.clientState !== 'object') {
          if (import.meta.env.DEV) {
            console.warn('[IDBPersister] Invalid clientState, discarding cache...');
          }
          await del(key, queryStore);
          return undefined;
        }
        
        // Validation 3: Ensure queries is an array (critical for areHookInputsEqual)
        if (!Array.isArray(client.clientState?.queries)) {
          if (import.meta.env.DEV) {
            console.warn('[IDBPersister] Invalid queries structure, discarding cache...');
          }
          await del(key, queryStore);
          return undefined;
        }
        
        // Validation 4: Ensure mutations is an array
        if (client.clientState.mutations && !Array.isArray(client.clientState.mutations)) {
          if (import.meta.env.DEV) {
            console.warn('[IDBPersister] Invalid mutations structure, discarding cache...');
          }
          await del(key, queryStore);
          return undefined;
        }
        
        // Validation 5: Ensure each query has valid queryKey array
        const hasInvalidQueries = client.clientState.queries.some(
          (q: any) => !q.queryKey || !Array.isArray(q.queryKey)
        );
        
        if (hasInvalidQueries) {
          if (import.meta.env.DEV) {
            console.warn('[IDBPersister] Found queries with invalid queryKey, discarding cache...');
          }
          await del(key, queryStore);
          return undefined;
        }
        
        // Validation 6: Ensure no queryKey contains undefined values (causes React comparison crashes)
        // This specifically fixes the "Cannot read properties of undefined (reading 'length')" error
        const hasUndefinedInQueryKey = client.clientState.queries.some(
          (q: any) => Array.isArray(q.queryKey) && q.queryKey.some((k: any) => k === undefined)
        );
        
        if (hasUndefinedInQueryKey) {
          if (import.meta.env.DEV) {
            console.warn('[IDBPersister] Found queries with undefined in queryKey, discarding cache...');
          }
          await del(key, queryStore);
          return undefined;
        }
        
        // Validation 7: Check for corrupted state (data undefined but dataUpdatedAt > 0)
        // This catches queries that were saved mid-flight or with incomplete hydration
        const hasCorruptedState = client.clientState.queries.some(
          (q: any) => q.state?.data === undefined && q.state?.dataUpdatedAt > 0
        );
        
        if (hasCorruptedState) {
          if (import.meta.env.DEV) {
            console.warn('[IDBPersister] Found queries with corrupted state, discarding cache...');
          }
          await del(key, queryStore);
          return undefined;
        }
        
        return client;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[IDBPersister] Failed to restore query client:', error);
        }
        // Clean corrupted cache silently
        try { await del(key, queryStore); } catch {}
        return undefined;
      }
    },
    removeClient: async () => {
      try {
        await del(key, queryStore);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[IDBPersister] Failed to remove query client:', error);
        }
      }
    },
  };
}
